<template>
	<v-main>
		<v-container fluid fill-height>
			<v-layout justify-center align-center>
				<v-card width="100%" outlined elevation-0>
					<v-toolbar flat class="border_bottom" shaped>
						<v-toolbar-title>Report clienti</v-toolbar-title>
						<v-spacer></v-spacer>
						<v-toolbar-items>
							<v-btn text title="Lista clienti" @click="apriLista()">
								<v-icon>mdi-account-multiple-outline</v-icon>
							</v-btn>
							<v-menu offset-y left>
								<template v-slot:activator="{ on, attrs }">
									<v-btn text v-bind="attrs" title="Altre azioni" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
								</template>
								<v-list dense>
									<v-list-item link :disabled="!canRecompute" @click="dialogRicalcola = true">
										<v-list-item-icon><v-icon small>mdi-camera-retake-outline</v-icon></v-list-item-icon>
										<v-list-item-title>Ricalcola {{ singleMonth ? monthLabel(singleMonth.month) : 'mese' }}</v-list-item-title>
									</v-list-item>
									<v-list-item link @click="esporta({})">
										<v-list-item-icon><v-icon small>mdi-arrow-collapse-down</v-icon></v-list-item-icon>
										<v-list-item-title>Esporta tutti i clienti</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</v-toolbar-items>
					</v-toolbar>

					<v-col cols="12">
						<FinancePeriodBar
							v-model="filtri"
							:platforms="channels"
							:loading="loading"
							:refreshing="refreshing"
							:refresh-progress="refreshProgress"
							:refresh-label="refreshLabel"
							@refresh="aggiorna"
						/>

						<div v-if="data" class="cu-status">
							<v-chip v-for="m in data.months" :key="m.month" small label outlined :color="status(m).color">
								<v-icon x-small left>{{ status(m).icon }}</v-icon>
								{{ monthLabel(m.month) }} · {{ status(m).label }}
							</v-chip>
							<span v-if="data.statsBuiltAt" class="cu-muted">Statistiche dei clienti aggiornate il {{ data.statsBuiltAt | moment('DD/MM/YYYY HH:mm') }}</span>
						</div>

						<v-tabs v-model="tab" class="mt-2 mb-3" background-color="transparent">
							<v-tab v-for="t in TABS" :key="t.value">{{ t.label }}</v-tab>
						</v-tabs>

						<div v-if="!data && loading" class="cu-empty">
							<v-progress-circular indeterminate color="primary" />
							<div class="cu-muted mt-3">Calcolo in corso: la prima apertura costruisce le statistiche di tutti i clienti e può richiedere un paio di minuti.</div>
						</div>
						<template v-else-if="data">
							<CustomersOverview
								v-if="tabValue === 'overview'"
								:report="data.report"
								:previous="data.previous"
								:lifetime="data.lifetime"
								:entry-products="data.entryProducts || []"
								:previous-label="previousLabel"
							/>
							<CustomersTimeline v-else-if="tabValue === 'timeline'" :series="data.series" />
							<CustomersCountries v-else-if="tabValue === 'countries'" :rows="data.report.countries || []" :multi-month="data.months.length > 1" />
							<CustomersSegments
								v-else-if="tabValue === 'segments'"
								:segments="data.segments || []"
								@list="s => apriLista({ segment: s.id })"
								@export="s => esporta({ segment: s.id })"
								@coupon="coupon"
							/>
							<CustomersCohorts v-else :cohorts="cohorts" :loading="loadingCohorts" />
						</template>
					</v-col>
				</v-card>
			</v-layout>
		</v-container>

		<v-dialog v-model="dialogRicalcola" max-width="480">
			<v-card>
				<v-card-title class="text-h6">Ricalcolare {{ singleMonth ? monthLabel(singleMonth.month) : '' }}?</v-card-title>
				<v-card-text>La fotografia del mese viene rifatta con i clienti di oggi. La versione precedente resta salvata nello storico del mese.</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="dialogRicalcola = false">Annulla</v-btn>
					<v-btn depressed color="primary" :loading="ricalcolo" @click="ricalcola">Ricalcola</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-main>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import FinancePeriodBar from '@/components/finance/FinancePeriodBar.vue';
import CustomersOverview from '@/components/finance/customers/CustomersOverview.vue';
import CustomersTimeline from '@/components/finance/customers/CustomersTimeline.vue';
import CustomersCountries from '@/components/finance/customers/CustomersCountries.vue';
import CustomersSegments from '@/components/finance/customers/CustomersSegments.vue';
import CustomersCohorts from '@/components/finance/customers/CustomersCohorts.vue';
import { refreshMixin } from '@/components/finance/refreshMixin';
import { putHandoff } from '@/components/core/pageHandoff';
import { openWithReturn } from '@/components/core/pageReturn';
import { periodMonths, previousMonth, monthLabel, addMonths, STATUS } from '@/components/finance/format';

const TABS = [
	{ value: 'overview', label: 'Panoramica' },
	{ value: 'timeline', label: 'Nel tempo' },
	{ value: 'countries', label: 'Nazioni' },
	{ value: 'segments', label: 'Segmenti' },
	{ value: 'cohorts', label: 'Coorti' },
];

// filtri della barra <-> query dell'URL (come il report finanziario)
const fromQuery = query => {
	const [py, pm] = previousMonth().split('-').map(Number);
	const period = ['month', 'quarter', 'year'].includes(query.period) ? query.period : 'month';
	return {
		period,
		year: +query.year || py,
		month: +query.month || pm,
		quarter: +query.quarter || Math.ceil(pm / 3),
		compare: ['prev', 'yoy'].includes(query.compare) ? query.compare : '',
		platform: query.platform || '',
	};
};

export default {
	name: 'FinanceCustomers',
	components: { FinancePeriodBar, CustomersOverview, CustomersTimeline, CustomersCountries, CustomersSegments, CustomersCohorts },
	// "Aggiorna" ricostruisce le statistiche dei clienti e ricalcola i mesi del periodo
	mixins: [refreshMixin('customers')],
	data() {
		return {
			TABS,
			tab: Math.max(
				0,
				TABS.findIndex(t => t.value === this.$route.query.tab),
			),
			filtri: fromQuery(this.$route.query),
			data: null,
			cohorts: [],
			cohortsKey: '',
			loading: false,
			loadingCohorts: false,
			channels: [],
			dialogRicalcola: false,
			ricalcolo: false,
		};
	},
	computed: {
		tabValue() {
			return (TABS[this.tab] || TABS[0]).value;
		},
		range() {
			return periodMonths(this.filtri);
		},
		previousLabel() {
			const months = this.data && this.data.previousMonths;
			if (!months || !months.length) return '';
			return months.length === 1 ? monthLabel(months[0]) : `${monthLabel(months[0])} – ${monthLabel(months[months.length - 1])}`;
		},
		singleMonth() {
			return this.data && this.data.months.length === 1 ? this.data.months[0] : null;
		},
		canRecompute() {
			return !!this.singleMonth && this.singleMonth.status !== 'open';
		},
	},
	watch: {
		filtri: {
			deep: true,
			handler() {
				this.scriviUrl();
				this.cohortsKey = '';
				this.carica();
			},
		},
		tab() {
			this.scriviUrl();
			this.caricaCoorti();
		},
		'$route.query.tab'(value) {
			const index = Math.max(
				0,
				TABS.findIndex(t => t.value === value),
			);
			if (index !== this.tab) this.tab = index;
		},
	},
	mounted() {
		this.carica();
	},
	methods: {
		...mapMutations({ setLogout: 'profile/setLogout' }),
		...mapActions({ fetchReport: 'finance/fetchCustomersReport', fetchCohorts: 'finance/fetchCohorts', recompute: 'finance/customersRecompute' }),
		monthLabel,
		status(m) {
			return STATUS[m.status] || STATUS.closed;
		},
		scriviUrl() {
			const f = this.filtri;
			const query = { period: f.period, year: String(f.year) };
			if (f.period === 'month') query.month = String(f.month);
			if (f.period === 'quarter') query.quarter = String(f.quarter);
			if (f.compare) query.compare = f.compare;
			if (f.platform) query.platform = f.platform;
			if (this.tab) query.tab = this.tabValue;
			if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.replace({ query }).catch(() => {});
		},
		async carica() {
			this.loading = true;
			try {
				this.data = await this.fetchReport({ from: this.range.from, to: this.range.to, platform: this.filtri.platform, compare: this.filtri.compare });
				if (!this.filtri.platform) this.channels = this.data.channels || [];
				this.cohortsKey = '';
				await this.caricaCoorti();
			} catch (err) {
				this.errore(err);
			} finally {
				this.loading = false;
			}
		},
		// le coorti si leggono solo quando si apre la scheda: 12 mesi fino all'ultimo del periodo
		async caricaCoorti() {
			if (this.tabValue !== 'cohorts') return;
			const to = this.range.to;
			const key = `${to}|${this.filtri.platform}`;
			if (this.cohortsKey === key) return;
			this.loadingCohorts = true;
			try {
				const res = await this.fetchCohorts({ from: addMonths(to, -11), to, platform: this.filtri.platform });
				this.cohorts = res.cohorts || [];
				this.cohortsKey = key;
			} catch (err) {
				this.errore(err);
			} finally {
				this.loadingCohorts = false;
			}
		},
		apriLista(filters = {}) {
			const query = { ...filters };
			if (this.filtri.platform) query.platform = this.filtri.platform;
			openWithReturn(this, { path: '/finance/customers/list', query }, { label: 'Torna al report clienti' });
		},
		esporta(filters) {
			this.exportAsync('customersreport', 'excel', { ...filters, platform: this.filtri.platform || undefined });
		},
		// coupon del segmento: la scheda Nuova regola coupon, con i clienti registrati del segmento gia' scelti
		async coupon(segment) {
			try {
				const res = await this.$store.dispatch('finance/fetchSegmentCustomers', { segment: segment.id, platform: this.filtri.platform });
				if (!res.customers.length) {
					this.notify('warning', 'Nel segmento non ci sono clienti registrati: il coupon si può limitare solo a clienti con un account sul sito.');
					return;
				}
				putHandoff('coupon-prefill', { name: `Coupon ${segment.label}`, event: 'PRODUCT_PROMOTION', customers: res.customers, total: res.total, limit: res.limit });
				openWithReturn(this, { path: '/ecommerce/coupon/new' }, { label: 'Torna al report clienti' });
			} catch (err) {
				this.errore(err);
			}
		},
		async ricalcola() {
			this.ricalcolo = true;
			try {
				await this.recompute(this.singleMonth.month);
				this.dialogRicalcola = false;
				this.notify('success', `${monthLabel(this.singleMonth.month)} ricalcolato`);
				await this.carica();
			} catch (err) {
				this.errore(err);
			} finally {
				this.ricalcolo = false;
			}
		},
		notify(color, text) {
			this.$store.dispatch('settings/updateSnackbar', { color, text, snackbar: true, mode: 'multi-line', timeout: 6000 });
		},
		errore(err) {
			const res = err.response;
			if (res && res.status === 401) {
				this.setLogout();
				this.$router.push('/login');
				return;
			}
			const errors = res && res.data && res.data.error;
			this.notify('error', Array.isArray(errors) ? errors.map(e => e.msg).join(', ') : (res && res.data && res.data.message) || err.message);
		},
	},
};
</script>

<style scoped>
.cu-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.cu-status {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px 10px;
	margin-top: 10px;
}
.cu-empty {
	text-align: center;
	padding: 60px 0;
}
</style>
